import React, { createContext, useContext, useState, useCallback } from 'react';
import { useAuth } from './authContext';

const AdminRegistrationContext = createContext();

// FIX 1: Move these constants OUTSIDE the component so they are stable
// and don't cause dependency errors in useCallback hooks.
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://7d515f15e81f.ngrok-free.app';
const ADMIN_REG_BASE = `${API_BASE_URL}/master/registration/api/v1/admin-registration`;

export const useAdminRegistration = () => {
  const context = useContext(AdminRegistrationContext);
  if (!context) {
    throw new Error('useAdminRegistration must be used within an AdminRegistrationProvider');
  }
  return context;
};

export const AdminRegistrationProvider = ({ children }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { makeAuthenticatedRequest, loginType } = useAuth();

  // Helper function to handle API responses
  const handleApiResponse = async (response) => {
    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.error || data.message || 'Request failed');
    }
    
    return data;
  };

  // Clear error state
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Create a new admin account
  const createAdmin = useCallback(async (adminData) => {
    if (loginType !== 'master') {
      throw new Error('Only master users can create admin accounts');
    }

    setLoading(true);
    setError(null);

    try {
      // FIX: ADMIN_REG_BASE is now global, so no dependency issue here
      const response = await makeAuthenticatedRequest(ADMIN_REG_BASE, {
        method: 'POST',
        body: JSON.stringify(adminData),
      });

      const result = await handleApiResponse(response);
      return result;
    } catch (err) {
      const errorMessage = err.message || 'Failed to create admin account';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [makeAuthenticatedRequest, loginType]);

  // List admins with pagination and filtering
  const listAdmins = useCallback(async (options = {}) => {
    if (loginType !== 'master') {
      throw new Error('Only master users can list admin accounts');
    }

    setLoading(true);
    setError(null);

    try {
      const queryParams = new URLSearchParams();
      
      if (options.page) queryParams.append('page', options.page);
      if (options.pageSize) queryParams.append('page_size', options.pageSize);
      if (options.accessStatus) queryParams.append('access_status', options.accessStatus);
      if (options.search) queryParams.append('search', options.search);
      if (options.sortBy) queryParams.append('sort_by', options.sortBy);
      if (options.sortOrder) queryParams.append('sort_order', options.sortOrder);

      const url = `${ADMIN_REG_BASE}?${queryParams.toString()}`;
      const response = await makeAuthenticatedRequest(url);

      const result = await handleApiResponse(response);
      return result;
    } catch (err) {
      const errorMessage = err.message || 'Failed to list admin accounts';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [makeAuthenticatedRequest, loginType]);

  // Get a specific admin by ID
  const getAdmin = useCallback(async (adminId) => {
    if (loginType !== 'master') {
      throw new Error('Only master users can view admin accounts');
    }

    setLoading(true);
    setError(null);

    try {
      const response = await makeAuthenticatedRequest(`${ADMIN_REG_BASE}/${adminId}`);
      const result = await handleApiResponse(response);
      return result;
    } catch (err) {
      const errorMessage = err.message || 'Failed to get admin account';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [makeAuthenticatedRequest, loginType]);

  // Update an admin account
  const updateAdmin = useCallback(async (adminId, updateData) => {
    if (loginType !== 'master') {
      throw new Error('Only master users can update admin accounts');
    }

    setLoading(true);
    setError(null);

    try {
      const response = await makeAuthenticatedRequest(`${ADMIN_REG_BASE}/${adminId}`, {
        method: 'PUT',
        body: JSON.stringify(updateData),
      });

      const result = await handleApiResponse(response);
      return result;
    } catch (err) {
      const errorMessage = err.message || 'Failed to update admin account';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [makeAuthenticatedRequest, loginType]);

  // Delete an admin account
  const deleteAdmin = useCallback(async (adminId) => {
    if (loginType !== 'master') {
      throw new Error('Only master users can delete admin accounts');
    }

    setLoading(true);
    setError(null);

    try {
      const response = await makeAuthenticatedRequest(`${ADMIN_REG_BASE}/${adminId}`, {
        method: 'DELETE',
      });

      const result = await handleApiResponse(response);
      return result;
    } catch (err) {
      const errorMessage = err.message || 'Failed to delete admin account';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [makeAuthenticatedRequest, loginType]);

  // Reset admin password
  const resetAdminPassword = useCallback(async (adminId, newPassword) => {
    if (loginType !== 'master') {
      throw new Error('Only master users can reset admin passwords');
    }

    setLoading(true);
    setError(null);

    try {
      const response = await makeAuthenticatedRequest(`${ADMIN_REG_BASE}/${adminId}/reset-password`, {
        method: 'POST',
        body: JSON.stringify({
          admin_id: adminId,
          new_password: newPassword,
        }),
      });

      const result = await handleApiResponse(response);
      return result;
    } catch (err) {
      const errorMessage = err.message || 'Failed to reset admin password';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [makeAuthenticatedRequest, loginType]);

  // Search admins
  const searchAdmins = useCallback(async (searchTerm, limit = 20) => {
    if (loginType !== 'master') {
      throw new Error('Only master users can search admin accounts');
    }

    setLoading(true);
    setError(null);

    try {
      const queryParams = new URLSearchParams({
        q: searchTerm,
        limit: limit.toString(),
      });

      const response = await makeAuthenticatedRequest(`${ADMIN_REG_BASE}/search?${queryParams.toString()}`);
      const result = await handleApiResponse(response);
      return result;
    } catch (err) {
      const errorMessage = err.message || 'Failed to search admin accounts';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [makeAuthenticatedRequest, loginType]);

  // Get admin statistics
  const getAdminStats = useCallback(async () => {
    if (loginType !== 'master') {
      throw new Error('Only master users can view admin statistics');
    }

    setLoading(true);
    setError(null);

    try {
      const response = await makeAuthenticatedRequest(`${ADMIN_REG_BASE}/stats`);
      const result = await handleApiResponse(response);
      return result;
    } catch (err) {
      const errorMessage = err.message || 'Failed to get admin statistics';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [makeAuthenticatedRequest, loginType]);

  // List registration history
  const listRegistrations = useCallback(async (page = 1, pageSize = 20) => {
    if (loginType !== 'master') {
      throw new Error('Only master users can view registration history');
    }

    setLoading(true);
    setError(null);

    try {
      const queryParams = new URLSearchParams({
        page: page.toString(),
        page_size: pageSize.toString(),
      });

      const response = await makeAuthenticatedRequest(`${ADMIN_REG_BASE}/registrations?${queryParams.toString()}`);
      const result = await handleApiResponse(response);
      return result;
    } catch (err) {
      const errorMessage = err.message || 'Failed to get registration history';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [makeAuthenticatedRequest, loginType]);

  // Get registration record for specific admin
  const getAdminRegistration = useCallback(async (adminId) => {
    if (loginType !== 'master') {
      throw new Error('Only master users can view registration records');
    }

    setLoading(true);
    setError(null);

    try {
      const response = await makeAuthenticatedRequest(`${ADMIN_REG_BASE}/${adminId}/registration`);
      const result = await handleApiResponse(response);
      return result;
    } catch (err) {
      const errorMessage = err.message || 'Failed to get registration record';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [makeAuthenticatedRequest, loginType]);

  // Perform bulk actions on multiple admins
  const bulkAction = useCallback(async (action, adminIds) => {
    if (loginType !== 'master') {
      throw new Error('Only master users can perform bulk actions');
    }

    if (!['activate', 'deactivate', 'suspend', 'delete'].includes(action)) {
      throw new Error('Invalid bulk action. Supported actions: activate, deactivate, suspend, delete');
    }

    if (!Array.isArray(adminIds) || adminIds.length === 0) {
      throw new Error('Admin IDs array is required for bulk actions');
    }

    if (adminIds.length > 50) {
      throw new Error('Maximum 50 admins can be processed at once');
    }

    setLoading(true);
    setError(null);

    try {
      const response = await makeAuthenticatedRequest(`${ADMIN_REG_BASE}/bulk`, {
        method: 'POST',
        body: JSON.stringify({
          action,
          admin_ids: adminIds,
        }),
      });

      const result = await handleApiResponse(response);
      return result;
    } catch (err) {
      const errorMessage = err.message || 'Failed to perform bulk action';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [makeAuthenticatedRequest, loginType]);

  // Health check for admin registration service
  const checkHealth = useCallback(async () => {
    try {
      const response = await makeAuthenticatedRequest(`${ADMIN_REG_BASE}/health`);
      const result = await handleApiResponse(response);
      return result;
    } catch (err) {
      const errorMessage = err.message || 'Health check failed';
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  }, [makeAuthenticatedRequest]);

  // Validation helpers
  // FIX: Added empty dependency array [] since this doesn't depend on any props/state
  const validateAdminData = useCallback((adminData) => {
    const errors = [];

    if (!adminData.admin_first_name || adminData.admin_first_name.trim().length < 2) {
      errors.push({ field: 'admin_first_name', message: 'First name must be at least 2 characters' });
    }

    if (!adminData.admin_last_name || adminData.admin_last_name.trim().length < 2) {
      errors.push({ field: 'admin_last_name', message: 'Last name must be at least 2 characters' });
    }

    if (!adminData.admin_email || !/\S+@\S+\.\S+/.test(adminData.admin_email)) {
      errors.push({ field: 'admin_email', message: 'Valid email address is required' });
    }

    if (!adminData.admin_password || adminData.admin_password.length < 8) {
      errors.push({ field: 'admin_password', message: 'Password must be at least 8 characters' });
    }

    if (adminData.access_status && !['active', 'inactive', 'suspended', 'pending'].includes(adminData.access_status)) {
      errors.push({ field: 'access_status', message: 'Invalid access status' });
    }

    return errors;
  }, []);

  // Validate password strength
  // FIX: Added empty dependency array []
  const validatePassword = useCallback((password) => {
    const errors = [];

    if (password.length < 8) {
      errors.push('Password must be at least 8 characters long');
    }

    if (!/[A-Z]/.test(password)) {
      errors.push('Password must contain at least one uppercase letter');
    }

    if (!/[a-z]/.test(password)) {
      errors.push('Password must contain at least one lowercase letter');
    }

    if (!/[0-9]/.test(password)) {
      errors.push('Password must contain at least one number');
    }

    // FIX 2: Removed unnecessary escape character `\` before `[` to fix "no-useless-escape" error
    if (!/[!@#$%^&*()_+\-=[\]{}|;:,.<>?]/.test(password)) {
      errors.push('Password must contain at least one special character');
    }

    return errors;
  }, []);

  const value = {
    // State
    loading,
    error,
    
    // Actions
    createAdmin,
    listAdmins,
    getAdmin,
    updateAdmin,
    deleteAdmin,
    resetAdminPassword,
    searchAdmins,
    getAdminStats,
    listRegistrations,
    getAdminRegistration,
    bulkAction,
    checkHealth,
    
    // Utilities
    clearError,
    validateAdminData,
    validatePassword,
    
    // Computed values
    isMasterUser: loginType === 'master',
  };

  return (
    <AdminRegistrationContext.Provider value={value}>
      {children}
    </AdminRegistrationContext.Provider>
  );
};